/**
 * Step 5 — Funding.
 *
 * Per `rcp-apply-now-step5-funding.html`.
 *
 * A11y fixes:
 *   - Method tiles: `<button role="radio">` + `aria-checked` (not `<div>`).
 *   - Amount presets: `aria-pressed` toggles.
 *   - Dollar field: `inputmode="decimal"` + `aria-valuetext`-style description.
 *
 * PCI: v3.0 does NOT touch card data (per rcp-security §A.7 confirmation).
 * Funding methods = ACH (external routing+account) or in-branch.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useApplyWizard } from './ApplyWizard';
import { useToast } from '../../components/a11y/AccessibleToast';
import { ArrowLeft, ArrowRight, Building2, DollarSign, Banknote } from '@retail-ops/panel-shell/fi';

type FundingMethod = 'ach-external' | 'in-branch';

const PRESET_AMOUNTS: ReadonlyArray<number> = [50, 100, 250, 500];

export function Step5Funding(): JSX.Element {
  const { setFundingAmount, gotoStep } = useApplyWizard();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [method, setMethod] = useState<FundingMethod | null>(null);
  const [amount, setAmount] = useState<string>('');

  function continueNext(): void {
    const parsed = Number(amount.replace(/,/g, ''));
    if (!method) {
      pushToast('error', 'Please pick a funding method');
      return;
    }
    if (!Number.isFinite(parsed) || parsed < 25) {
      pushToast('error', 'Please enter a valid opening deposit', 'A minimum of $25 is required to open your account.');
      return;
    }
    setFundingAmount(parsed);
    navigate('/apply/success');
  }

  return (
    <section aria-labelledby="step5-heading">
      <h1 id="step5-heading" tabIndex={-1}>Fund your account</h1>
      <p className="lead" style={{ marginBottom: 24 }}>
        How would you like to make your opening deposit?
      </p>

      <fieldset className="card" style={{ border: '1px solid var(--surface-border)' }}>
        <legend style={{ padding: '0 8px', fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>
          Funding method
        </legend>
        <div
          role="radiogroup"
          aria-label="Funding method"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}
        >
          <MethodCard
            icon={<Banknote aria-hidden="true" size={24} />}
            title="Transfer from another bank"
            description="We'll debit your existing bank account. Takes 1-3 business days."
            selected={method === 'ach-external'}
            onClick={() => setMethod('ach-external')}
          />
          <MethodCard
            icon={<Building2 aria-hidden="true" size={24} />}
            title="Visit a branch"
            description="Drop off cash or a check at any PCB branch."
            selected={method === 'in-branch'}
            onClick={() => setMethod('in-branch')}
          />
        </div>
      </fieldset>

      <div className="card">
        <h2>Opening deposit</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }} role="group" aria-label="Quick amounts">
          {PRESET_AMOUNTS.map((preset) => {
            const isPressed = amount === String(preset);
            return (
              <button
                key={preset}
                type="button"
                aria-pressed={isPressed}
                onClick={() => setAmount(String(preset))}
                className="btn btn-ghost"
                style={{
                  borderColor: isPressed ? 'var(--pcb-blue)' : 'var(--surface-border)',
                  background: isPressed ? 'var(--surface-info-soft)' : 'transparent',
                }}
              >
                ${preset}
              </button>
            );
          })}
        </div>
        <div className="field" style={{ maxWidth: 280 }}>
          <label htmlFor="amount">Amount</label>
          <div style={{ position: 'relative' }}>
            <DollarSign
              aria-hidden="true"
              size={16}
              style={{ position: 'absolute', left: 12, top: 12, color: 'var(--ink-500)' }}
            />
            <input
              id="amount"
              type="text"
              inputMode="decimal"
              autoComplete="transaction-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              className="input mono"
              style={{ paddingLeft: 34 }}
              aria-describedby="amount-hint"
              placeholder="0.00"
            />
          </div>
          <span id="amount-hint" className="hint">
            Minimum $25. Use digits only (e.g., 100.00).
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        <button type="button" className="btn btn-ghost" onClick={() => gotoStep(3)}>
          <ArrowLeft aria-hidden="true" size={16} /> Back
        </button>
        <button type="button" className="btn btn-primary" onClick={continueNext}>
          Submit application
          <ArrowRight aria-hidden="true" size={16} />
        </button>
      </div>
    </section>
  );
}

function MethodCard({
  icon,
  title,
  description,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      className="card"
      style={{
        textAlign: 'left',
        borderColor: selected ? 'var(--pcb-blue)' : 'var(--surface-border)',
        borderWidth: selected ? 2 : 1,
        cursor: 'pointer',
        margin: 0,
      }}
    >
      {icon}
      <h3 style={{ margin: '10px 0 4px 0' }}>{title}</h3>
      <p style={{ margin: 0, color: 'var(--ink-700)', fontSize: 13 }}>{description}</p>
    </button>
  );
}
